import useDeepCompareEffect from 'use-deep-compare-effect'
import { useEffect } from 'react'
import { useMediaQuery } from 'react-responsive'
import { useDispatch } from 'react-redux'
import Cookies from 'js-cookie'
import { addTrackingScript, getTrackingState, ReportViewEventArgs, TrackingScriptState } from './tracking/tracking'
import { WizardFlowType } from './@types/wizardFlowType'
import { reportTrackingViewEvent } from './tracking/thunks'

export const useReportViewEvent = (args?: ReportViewEventArgs): void => {
  const dispatch = useDispatch()
  const formattedArgs: ReportViewEventArgs = args ? args : { pageTitle: 'unknown' }
  useDeepCompareEffect(() => {
    if (formattedArgs.pageTitle === 'unknown') return
    dispatch(reportTrackingViewEvent(formattedArgs))
  }, [formattedArgs])
}

const TEALIUM_CONSENT_COOKIE = 'cmp_activation'

export const useAddTrackingScript = (): void => {
  const configurationData = window.IS24?.RLE?.configuration
  const tealiumEnabled: boolean = configurationData?.tealium ?? true

  if (!tealiumEnabled) Cookies.set(TEALIUM_CONSENT_COOKIE, 'false')

  useEffect(() => {
    getTrackingState().then((scriptState) => {
      if (scriptState === TrackingScriptState.ABSENT) {
        addTrackingScript()
      }
    })
  }, [])
}

export const useAddGoogleMapsScript = (): void => {
  useEffect(() => {
    const hasGoogleMapsScript = Array.from(document.querySelectorAll('script')).some(
      (script) => script.src && script.src.match(/maps\.googleapis\.com.*places/)
    )

    if (!hasGoogleMapsScript) {
      const src = `${process.env.GOOGLE_MAPS_API_URL}/js?key=${process.env.GOOGLE_MAPS_API_KEY}&libraries=places`
      const script = document.createElement('script')
      script.src = src
      script.async = true
      document.head.appendChild(script)
    }
  }, [])
}

const breakpoints = {
  palmMax: 668,
}

export const useResponsive = (): Record<string, boolean> => ({
  isMobile: useMediaQuery({ maxWidth: breakpoints.palmMax }),
})

export const useTouchDetection = (): void => {
  useEffect(() => {
    if ('ontouchstart' in document.documentElement) {
      document.body.classList.add('has-touch')
    }
  }, [])
}

const useWizardFlow = (): WizardFlowType => {
  const widget = document.getElementById('rle-wizard')
  const dataFlowType = widget?.getAttribute('data-flow-type') || 'rle'
  const flowTypeMapping: { [k: string]: WizardFlowType } = {
    rle: WizardFlowType.RLE,
    solar: WizardFlowType.SOLAR,
  }
  return flowTypeMapping[dataFlowType] ?? WizardFlowType.IBW
}

export { useWizardFlow }
